import { FC, useState, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'antd/lib/form/Form';

import {
  Button,
  ButtonsWrapper,
  CheckboxInput,
  Form,
  ICONS,
  PLATFORM_THEME_COLORS,
  Typography,
  TYPOGRAPHY_SIZE,
  TextInput,
  ColorSelect,
  SwitchInput,
  Icon,
  PopoverTooltip,
} from '@thinknet-gmbh/platform-common-ui';
import {
  DEFAULT_NAME_FIELD_REGEX,
  PERMISSION_ACTIONS,
  PLATFORM_PERMISSIONS,
} from '@thinknet-gmbh/platform-common';

import { SETTINGS_PAGES } from 'src/pages/settings/settings.constants';
import PermissionCheck from 'src/components/permission-check';

import CommonFormList from './common-page-form-list.component';
import { COMMON_PAGE_OPTIONS, COMMON_PAGE_TYPES_DATA } from './common-page.constants';
import {
  RoundColorLabel,
  HeaderContainer,
  InfoWrapper,
  MainWrapper,
  InfoWrapperItem,
  StyledBillableButton,
  StyledLabelContainer,
} from './common-page.styles';
import { ICommonLayoutProps } from './common-page.interfaces';

const CommonPage: FC<ICommonLayoutProps> = ({ pageType, data, onFinish, onDelete }) => {
  const { t } = useTranslation(['common', 'settings']);
  const [form] = useForm();
  const [commonLayoutOption, setCommonLayoutOption] = useState<string | null>(null);
  const { title, addBtn, infoLabel, placeholder, formItemLabel } = COMMON_PAGE_TYPES_DATA[pageType];

  const onCancel = () => {
    setCommonLayoutOption(null);
    form.resetFields();
  };

  const onFormFinish = (values: any) => {
    onFinish(values);
    onCancel();
  };

  return (
    <Form layout="vertical" form={form} onFinish={onFormFinish}>
      <MainWrapper>
        {commonLayoutOption === COMMON_PAGE_OPTIONS.edit ? (
          <CommonFormList data={data} onDelete={onDelete} pageType={pageType} />
        ) : (
          <>
            <HeaderContainer>
              <Typography weight="bold" size={TYPOGRAPHY_SIZE.medium}>
                {t(title)}
              </Typography>
              <ButtonsWrapper>
                <PermissionCheck
                  permission={
                    pageType === SETTINGS_PAGES.documentsLabels
                      ? PLATFORM_PERMISSIONS.companyManagement
                      : PLATFORM_PERMISSIONS.companySettings
                  }
                  action={PERMISSION_ACTIONS.create}
                >
                  <Button
                    type="primary"
                    ghost
                    icon={ICONS.plus}
                    iconColor={PLATFORM_THEME_COLORS.primary.blue}
                    onClick={() => setCommonLayoutOption(COMMON_PAGE_OPTIONS.create)}
                  >
                    {t(addBtn)}
                  </Button>
                </PermissionCheck>
                <PermissionCheck
                  permission={
                    pageType === SETTINGS_PAGES.documentsLabels
                      ? PLATFORM_PERMISSIONS.companyManagement
                      : PLATFORM_PERMISSIONS.companySettings
                  }
                  action={PERMISSION_ACTIONS.create}
                >
                  <Button
                    type="primary"
                    ghost
                    icon={ICONS.editAlt}
                    iconColor={PLATFORM_THEME_COLORS.primary.blue}
                    onClick={() => setCommonLayoutOption(COMMON_PAGE_OPTIONS.edit)}
                  >
                    {t('common:buttons.edit')}
                  </Button>
                </PermissionCheck>
              </ButtonsWrapper>
            </HeaderContainer>
            <InfoWrapper>
              <Typography weight="bold">{`${t(infoLabel)}:`}</Typography>
              <div>
                {data.map(item => (
                  <InfoWrapperItem key={item._id}>
                    {item.color && <RoundColorLabel color={item.color} />}
                    <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
                      {item.name}
                    </Typography>
                  </InfoWrapperItem>
                ))}
              </div>
            </InfoWrapper>
            {commonLayoutOption === COMMON_PAGE_OPTIONS.create && (
              <>
                <Form.Item
                  name="name"
                  label={t(formItemLabel)}
                  rules={[
                    {
                      required: true,
                      message:
                        pageType === SETTINGS_PAGES.absence
                          ? t('settings:companySettings.exceptions.requiredAbsenceReason')
                          : t('common:validation.error.labelRequired'),
                    },
                    {
                      pattern: DEFAULT_NAME_FIELD_REGEX,
                      message: t('common:validation.error.incorrectFormat'),
                    },
                  ]}
                >
                  <TextInput placeholder={t(placeholder)} maxLength={50} />
                </Form.Item>
                {pageType === SETTINGS_PAGES.absence && (
                  <Fragment>
                    <Form.Item
                      name="color"
                      label={t('settings:companySettings.labels.absenceColor')}
                      rules={[
                        { required: true, message: t('common:validation.error.fieldRequired') },
                      ]}
                    >
                      <ColorSelect
                        translate={t}
                        placeholder={t('settings:companySettings.labels.chooseColor')}
                      />
                    </Form.Item>
                    <StyledBillableButton>
                      <StyledLabelContainer>
                        <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
                          {t('settings:companySettings.labels.absencePaymentStatus')}
                        </Typography>
                        <PopoverTooltip
                          placement="top"
                          overlayInnerStyle={{ width: '314px' }}
                          trigger={'hover'}
                          overlay={
                            <Typography fontColor={PLATFORM_THEME_COLORS.primary.white}>
                              {t('settings:companySettings.labels.popupOverlayText')}
                            </Typography>
                          }
                        >
                          <div>
                            <Icon
                              icon={ICONS.infoCircleRegular}
                              color={PLATFORM_THEME_COLORS.primary.blue}
                              size={20}
                            />
                          </div>
                        </PopoverTooltip>
                      </StyledLabelContainer>

                      <Form.Item name="absencePaymentStatus" valuePropName="checked">
                        <SwitchInput defaultChecked={false} />
                      </Form.Item>
                    </StyledBillableButton>
                  </Fragment>
                )}
                {pageType === SETTINGS_PAGES.documentsLabels && (
                  <Form.Item name="availableForEmployee" valuePropName="checked">
                    <CheckboxInput>
                      {t('settings:documentsLabels.availableForEmployee')}
                    </CheckboxInput>
                  </Form.Item>
                )}
              </>
            )}
          </>
        )}
        {commonLayoutOption !== null && (
          <ButtonsWrapper>
            <Button htmlType="submit" type="primary">
              {commonLayoutOption === COMMON_PAGE_OPTIONS.create
                ? t('common:buttons.add')
                : t('common:buttons.change')}
            </Button>
            <Button onClick={onCancel}>{t('common:buttons.cancel')}</Button>
          </ButtonsWrapper>
        )}
      </MainWrapper>
    </Form>
  );
};

export default CommonPage;
